import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, XCircle, Info, AlertTriangle, ExternalLink, Eye, ChevronDown, ChevronUp, Bell, CheckCircle, ArrowRight, X, Trash2, Check } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { formatDateDisplay } from '../utils/dateUtils';
import { hasModulePermission } from '../utils/permissionUtils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Mapping of alert categories to permission modules
const ALERT_CATEGORY_TO_MODULE = {
  // Motorcycle/Insurance related
  'insurance': 'insurance',
  'registration': 'motorcycles',
  // Rider related
  'license': 'riders',
  'license_expiring': 'riders',
  'passport': 'riders',
  'emirates_id': 'riders',
  'visa': 'riders',
  'ohc': 'riders',
  'ohc_expiring': 'riders',
  'food_permit': 'riders',
  'food_permit_expiring': 'riders',
  // Data upload reminders
  'data_upload_fines': 'fines',
  'data_upload_insurance': 'insurance',
  'data_upload_salik': 'salik',
  // Overrule logs
  'overrule_pending': 'riders',
  // Battery swaps
  'battery_mismatch': 'batterySwaps',
  'odo_anomaly': 'batterySwaps',
  // Maintenance
  'maintenance': 'maintenance',
  // Default fallback - dashboard
  'default': 'dashboard'
};

const CriticalAlerts = ({ user }) => {
  const [alerts, setAlerts] = useState([]);
  const [replacementNotifications, setReplacementNotifications] = useState([]);
  const [mismatchAlerts, setMismatchAlerts] = useState([]);
  const [odoAnomalyAlerts, setOdoAnomalyAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem('criticalAlertsExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [dismissedReplacements, setDismissedReplacements] = useState([]);
  const [selectedOdoAlert, setSelectedOdoAlert] = useState(null);
  const [odoAlertDialogOpen, setOdoAlertDialogOpen] = useState(false);
  const navigate = useNavigate();

  // Check if user has permission to see an alert based on its category
  const canViewAlert = (alertCategory) => {
    // Admin and Super Admin can see all alerts
    if (user?.role === 'Super Admin' || user?.role === 'Admin') {
      return true;
    }
    
    const module = ALERT_CATEGORY_TO_MODULE[alertCategory] || ALERT_CATEGORY_TO_MODULE['default'];
    return hasModulePermission(user, module, 'view');
  };

  // Filter alerts based on user permissions and dismissed items
  const filteredAlerts = useMemo(() => {
    const permissionFiltered = alerts.filter(alert => canViewAlert(alert.category));
    // Filter out dismissed items from each alert
    return permissionFiltered.map(alert => ({
      ...alert,
      items: alert.items?.filter(item => !dismissedAlerts.some(d => d.item_id === item.id && d.category === alert.category)) || [],
      count: (alert.items?.filter(item => !dismissedAlerts.some(d => d.item_id === item.id && d.category === alert.category)) || []).length
    })).filter(alert => alert.count > 0);
  }, [alerts, user, dismissedAlerts]);

  // Filter mismatch alerts based on batterySwaps permission
  const filteredMismatchAlerts = useMemo(() => {
    if (!canViewAlert('battery_mismatch')) return [];
    return mismatchAlerts;
  }, [mismatchAlerts, user]);

  // Filter odo anomaly alerts based on batterySwaps permission
  const filteredOdoAnomalyAlerts = useMemo(() => {
    if (!canViewAlert('odo_anomaly')) return [];
    return odoAnomalyAlerts;
  }, [odoAnomalyAlerts, user]);

  // Filter replacement notifications based on motorcycles permission
  const filteredReplacementNotifications = useMemo(() => {
    if (!hasModulePermission(user, 'motorcycles', 'view')) return [];
    return replacementNotifications.filter(n => !dismissedReplacements.includes(n.id));
  }, [replacementNotifications, dismissedReplacements, user]);

  useEffect(() => {
    fetchAlerts();
    fetchReplacementNotifications();
    fetchMismatchAlerts();
    fetchOdoAnomalyAlerts();
    fetchDismissedAlerts();
    // Refresh alerts every 5 minutes
    const interval = setInterval(() => {
      fetchAlerts();
      fetchReplacementNotifications();
      fetchMismatchAlerts();
      fetchOdoAnomalyAlerts();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    localStorage.setItem('criticalAlertsExpanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/api/critical-alerts`, { headers: getAuthHeader() });
      console.log('Critical alerts response:', response.data);
      setAlerts(response.data.alerts || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      setLoading(false);
    }
  };

  const fetchReplacementNotifications = async () => {
    // Only fetch for admin, fleet_manager, and bike_technician
    const allowedRoles = ['Admin', 'fleet_manager', 'bike_technician'];
    if (!user || !allowedRoles.includes(user.role)) {
      return;
    }

    try {
      const response = await axios.get(
        `${API}/motorcycles/replacement-notifications`,
        { headers: getAuthHeader() }
      );
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setReplacementNotifications(response.data);
      } else {
        console.warn('Replacement notifications response is not an array:', response.data);
        setReplacementNotifications([]);
      }
    } catch (error) {
      console.error('Error fetching replacement notifications:', error);
      setReplacementNotifications([]);
    }
  };

  const fetchMismatchAlerts = async () => {
    // Only fetch for Admin and Super Admin
    const allowedRoles = ['Admin', 'Super Admin'];
    if (!user || !allowedRoles.includes(user.role)) {
      return;
    }

    try {
      const response = await axios.get(
        `${API}/api/battery-mismatch-alerts?status=pending`,
        { headers: getAuthHeader() }
      );
      setMismatchAlerts(response.data || []);
    } catch (error) {
      console.error('Error fetching mismatch alerts:', error);
      setMismatchAlerts([]);
    }
  };

  const fetchOdoAnomalyAlerts = async () => {
    // Only fetch for Admin and Super Admin
    const allowedRoles = ['Admin', 'Super Admin'];
    if (!user || !allowedRoles.includes(user.role)) {
      return;
    }

    try {
      const response = await axios.get(
        `${API}/api/critical-alerts/odo-anomalies`,
        { headers: getAuthHeader() }
      );
      setOdoAnomalyAlerts(response.data || []);
    } catch (error) {
      console.error('Error fetching ODO anomaly alerts:', error);
      setOdoAnomalyAlerts([]);
    }
  };

  const fetchDismissedAlerts = async () => {
    try {
      const response = await axios.get(
        `${API}/api/critical-alerts/dismissed`,
        { headers: getAuthHeader() }
      );
      setDismissedAlerts(response.data || []);
    } catch (error) {
      console.error('Error fetching dismissed alerts:', error);
      setDismissedAlerts([]);
    }
  };

  // Handle ODO alert click - open dialog with Keep/Delete options
  const handleOdoAlertClick = (alert, e) => {
    e.stopPropagation();
    setSelectedOdoAlert(alert);
    setOdoAlertDialogOpen(true);
  };

  // Keep the alert (just close dialog)
  const handleKeepOdoAlert = () => {
    setOdoAlertDialogOpen(false);
    setSelectedOdoAlert(null);
  };

  // Delete/Ignore the ODO alert
  const handleDeleteOdoAlert = async () => {
    if (!selectedOdoAlert) return;
    
    try {
      await axios.delete(
        `${API}/api/critical-alerts/odo-anomalies/${selectedOdoAlert.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('Alert deleted successfully');
      // Remove from local state
      setOdoAnomalyAlerts(prev => prev.filter(a => a.id !== selectedOdoAlert.id));
      setOdoAlertDialogOpen(false);
      setSelectedOdoAlert(null);
    } catch (error) {
      console.error('Error deleting ODO alert:', error);
      toast.error('Failed to delete alert');
    }
  };

  // Dismiss a critical alert item (expiring documents)
  const handleDismissAlert = async () => {
    if (!selectedItem || !selectedAlert) return;
    
    try {
      await axios.post(
        `${API}/api/critical-alerts/dismiss`,
        { 
          item_id: selectedItem.id, 
          category: selectedAlert.category 
        },
        { headers: getAuthHeader() }
      );
      toast.success('Alert dismissed successfully');
      // Add to local dismissed alerts
      setDismissedAlerts(prev => [...prev, { item_id: selectedItem.id, category: selectedAlert.category }]);
      setDetailDialogOpen(false);
      setSelectedItem(null);
      setItemDetails(null);
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };

  // Navigate to view item details
  const handleNavigateToItem = () => {
    if (!itemDetails) return;

    if (itemDetails.plate_number && itemDetails._detail_kind !== 'overrule') {
      // It's a motorcycle
      navigate(`/motorcycles?id=${itemDetails.id}`);
    } else if (itemDetails._detail_kind === 'overrule') {
      // Overrule alert — jump to the affected rider (by name) so user
      // can correct the bike-assignment documents.
      const q = encodeURIComponent(itemDetails.rider_name || '');
      navigate(`/riders?search=${q}`);
    } else if (itemDetails.name) {
      // It's a rider
      navigate(`/riders?id=${itemDetails.id}`);
    }
    setDetailDialogOpen(false);
  };

  const handleAlertClick = (alert) => {
    if (alert.count > 0 && alert.items && alert.items.length > 0) {
      setSelectedAlert(alert);
      setDialogOpen(true);
    }
  };

  const handleViewDetails = async (item) => {
    setSelectedItem(item);
    setItemDetails(null);

    // Categories that map to /api/motorcycles vs /api/riders vs render-as-is
    const cat = selectedAlert?.category || '';
    const isMotorcycle = cat === 'insurance' || cat === 'registration';
    const isRider = ['license', 'passport', 'emirates_id', 'visa', 'ohc', 'food_permit'].some(
      (k) => cat.includes(k)
    );
    const isOverrule = cat === 'overrule_pending';

    try {
      if (isMotorcycle) {
        const response = await axios.get(`${API}/api/motorcycles`, { headers: getAuthHeader() });
        const motorcycle = response.data.find((m) => m.id === item.id);
        setItemDetails(motorcycle || item);
      } else if (isRider) {
        const response = await axios.get(`${API}/api/riders`, { headers: getAuthHeader() });
        const rider = response.data.find((r) => r.id === item.id);
        // Merge fetched rider record with the alert item so any extra
        // fields (e.g. visa_expiry_date) supplied by the alert payload
        // are preserved if the rider record is missing them.
        setItemDetails({ ...item, ...(rider || {}) });
      } else if (isOverrule) {
        // Overrule items carry the rider_name + bike_plate_number directly
        // in the alert payload. No extra fetch needed — render those
        // fields in the dialog via the dedicated branch below.
        setItemDetails({ ...item, _detail_kind: 'overrule' });
      } else {
        // Generic fallback — render whatever fields the alert provided so
        // the dialog body is never blank.
        setItemDetails({ ...item, _detail_kind: 'generic' });
      }
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch item details:', error);
      // Even on error, open the dialog with whatever we have so the
      // user isn't stuck staring at a frozen UI.
      setItemDetails({ ...item, _detail_kind: 'generic' });
      setDetailDialogOpen(true);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'danger':
        return <XCircle className="h-5 w-5" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5" />;
      case 'info':
        return <Info className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getAlertClass = (type) => {
    switch (type) {
      case 'danger':
        return 'border-red-500 bg-red-50 text-red-900';
      case 'warning':
        return 'border-yellow-500 bg-yellow-50 text-yellow-900';
      case 'info':
        return 'border-blue-500 bg-blue-50 text-blue-900';
      default:
        return 'border-gray-500 bg-gray-50 text-gray-900';
    }
  };

  const formatDate = (dateStr) => {
    return formatDateDisplay(dateStr);
  };

  // Use filtered alerts and notifications based on permissions
  const totalFilteredAlerts = filteredAlerts.reduce((sum, alert) => sum + (alert.count || 0), 0) 
    + filteredReplacementNotifications.length 
    + filteredMismatchAlerts.length 
    + filteredOdoAnomalyAlerts.length;

  if (loading || (filteredAlerts.length === 0 && filteredReplacementNotifications.length === 0 && filteredMismatchAlerts.length === 0 && filteredOdoAnomalyAlerts.length === 0)) {
    return null;
  }

  return (
    <>
      <div className="mb-6">
        {/* Collapsed View */}
        {!isExpanded && (
          <div 
            className="bg-gradient-to-r from-red-50 to-yellow-50 border border-red-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
            onClick={toggleExpanded}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-red-600 animate-pulse" />
                <div>
                  <p className="font-semibold text-sm text-gray-900">
                    {filteredAlerts.length} Critical Alert{filteredAlerts.length !== 1 ? 's' : ''} ({totalFilteredAlerts} item{totalFilteredAlerts !== 1 ? 's' : ''})
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">Click to expand and view details</p>
                </div>
              </div>
              <ChevronDown className="h-5 w-5 text-gray-600" />
            </div>
          </div>
        )}

        {/* Expanded View */}
        {isExpanded && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Critical Alerts
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleExpanded}
                className="h-7 px-2"
              >
                <ChevronUp className="h-4 w-4 mr-1" />
                Collapse
              </Button>
            </div>
            
            {filteredAlerts.map((alert, index) => (
              <Alert 
                key={index} 
                className={`relative ${getAlertClass(alert.type)} ${alert.count > 0 ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} 
                role="alert"
                onClick={() => handleAlertClick(alert)}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1">
                    <AlertTitle className="font-semibold text-sm flex items-center gap-2">
                      {alert.title}
                      {alert.count > 0 && (
                        <ExternalLink className="h-4 w-4 opacity-50" />
                      )}
                    </AlertTitle>
                    <AlertDescription className="text-sm mt-1">
                      {alert.description}
                      {alert.count > 0 && (
                        <span className="block mt-1 text-xs font-medium opacity-75">
                          Click to view details
                        </span>
                      )}
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            ))}

            {/* Replacement Notifications - Bikes Ready to Return */}
            {filteredReplacementNotifications.map((notification) => (
              <Alert 
                key={`replacement-${notification.id}`}
                className="border-green-500 bg-green-50 text-green-900"
                role="alert"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <AlertTitle className="font-semibold text-sm">
                      Motorcycle Repaired & Ready to Return
                    </AlertTitle>
                    <AlertDescription className="text-sm mt-1">
                      <p className="mb-2">{notification.message}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
                        <div>
                          <p className="text-green-600 font-medium">Original Motorcycle</p>
                          <p className="text-green-900 font-semibold">{notification.original_bike_plate}</p>
                        </div>
                        <div>
                          <p className="text-green-600 font-medium">Replacement Motorcycle</p>
                          <p className="text-green-900 font-semibold">{notification.replacement_bike_plate}</p>
                        </div>
                        {notification.rider_name && (
                          <div>
                            <p className="text-green-600 font-medium">Rider</p>
                            <p className="text-green-900">{notification.rider_name}</p>
                          </div>
                        )}
                        {notification.lessee_name && (
                          <div>
                            <p className="text-green-600 font-medium">Lessee</p>
                            <p className="text-green-900">{notification.lessee_name}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/motorcycles?tab=replacements');
                          }}
                        >
                          Return Motorcycle to Rider
                          <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                        <Button 
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDismissedReplacements([...dismissedReplacements, notification.id]);
                          }}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Dismiss
                        </Button>
                      </div>
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            ))}

            {/* Battery Mismatch Alerts */}
            {filteredMismatchAlerts.map((alert) => (
              <Alert 
                key={`mismatch-${alert.id}`}
                className="border-orange-500 bg-orange-50 text-orange-900 cursor-pointer hover:shadow-md transition-shadow"
                role="alert"
                onClick={() => navigate('/battery-swaps')}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <AlertTitle className="font-semibold text-sm flex items-center gap-2">
                      Battery Mismatch Alert
                      <ExternalLink className="h-4 w-4 opacity-50" />
                    </AlertTitle>
                    <AlertDescription className="text-sm mt-1">
                      <p className="mb-2">Rider returned different batteries than expected</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs mt-2">
                        <div>
                          <p className="text-orange-600 font-medium">Rider</p>
                          <p className="text-orange-900 font-semibold">{alert.rider_name}</p>
                        </div>
                        <div>
                          <p className="text-orange-600 font-medium">Bike</p>
                          <p className="text-orange-900 font-semibold">{alert.bike_number}</p>
                        </div>
                        <div>
                          <p className="text-orange-600 font-medium">Date</p>
                          <p className="text-orange-900">{formatDateDisplay(alert.created_at)}</p>
                        </div>
                      </div>
                      <p className="mt-2 text-xs opacity-75">Click to view details on Battery Swaps page</p>
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            ))}

            {/* ODO Anomaly Alerts */}
            {filteredOdoAnomalyAlerts.map((alert) => (
              <Alert 
                key={`odo-${alert.id}`}
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  alert.odo_alert_type === 'exceeds_250km' 
                    ? 'border-yellow-500 bg-yellow-50 text-yellow-900'
                    : 'border-red-500 bg-red-50 text-red-900'
                }`}
                role="alert"
                onClick={(e) => handleOdoAlertClick(alert, e)}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <AlertTitle className="font-semibold text-sm flex items-center gap-2">
                      ODO Reading Anomaly
                      <ExternalLink className="h-4 w-4 opacity-50" />
                    </AlertTitle>
                    <AlertDescription className="text-sm mt-1">
                      <p className="mb-2 font-medium">
                        {alert.odo_alert_type === 'zero_reading' && '⚠️ ODO reading is zero or invalid'}
                        {alert.odo_alert_type === 'lower_than_previous' && '⚠️ ODO reading is lower than previous'}
                        {alert.odo_alert_type === 'exceeds_250km' && '⚠️ ODO reading exceeds 250 km from previous'}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
                        <div>
                          <p className={`${alert.odo_alert_type === 'exceeds_250km' ? 'text-yellow-600' : 'text-red-600'} font-medium`}>Rider</p>
                          <p className={`${alert.odo_alert_type === 'exceeds_250km' ? 'text-yellow-900' : 'text-red-900'} font-semibold`}>{alert.rider_name}</p>
                        </div>
                        <div>
                          <p className={`${alert.odo_alert_type === 'exceeds_250km' ? 'text-yellow-600' : 'text-red-600'} font-medium`}>Bike</p>
                          <p className={`${alert.odo_alert_type === 'exceeds_250km' ? 'text-yellow-900' : 'text-red-900'} font-semibold`}>{alert.bike_plate_number}</p>
                        </div>
                        <div>
                          <p className={`${alert.odo_alert_type === 'exceeds_250km' ? 'text-yellow-600' : 'text-red-600'} font-medium`}>Previous ODO</p>
                          <p className={`${alert.odo_alert_type === 'exceeds_250km' ? 'text-yellow-900' : 'text-red-900'}`}>{alert.previous_odo} km</p>
                        </div>
                        <div>
                          <p className={`${alert.odo_alert_type === 'exceeds_250km' ? 'text-yellow-600' : 'text-red-600'} font-medium`}>Current ODO</p>
                          <p className={`${alert.odo_alert_type === 'exceeds_250km' ? 'text-yellow-900' : 'text-red-900'}`}>{alert.current_odo} km</p>
                        </div>
                      </div>
                      <p className="mt-2 text-xs opacity-75">Recorded: {formatDateDisplay(alert.created_at)} | Click to view details</p>
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            ))}
          </div>
        )}
      </div>

      {/* ODO Alert Action Dialog */}
      <Dialog open={odoAlertDialogOpen} onOpenChange={setOdoAlertDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              ODO Reading Anomaly
            </DialogTitle>
            <DialogDescription>
              Choose how to handle this alert
            </DialogDescription>
          </DialogHeader>
          
          {selectedOdoAlert && (
            <div className="space-y-4 py-4">
              <div className={`p-4 rounded-lg ${
                selectedOdoAlert.odo_alert_type === 'exceeds_250km' 
                  ? 'bg-yellow-50 border border-yellow-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className="font-medium mb-2">
                  {selectedOdoAlert.odo_alert_type === 'zero_reading' && '⚠️ ODO reading is zero or invalid'}
                  {selectedOdoAlert.odo_alert_type === 'lower_than_previous' && '⚠️ ODO reading is lower than previous'}
                  {selectedOdoAlert.odo_alert_type === 'exceeds_250km' && '⚠️ ODO reading exceeds 250 km from previous'}
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Rider</p>
                    <p className="font-semibold">{selectedOdoAlert.rider_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Bike</p>
                    <p className="font-semibold">{selectedOdoAlert.bike_plate_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Previous ODO</p>
                    <p>{selectedOdoAlert.previous_odo} km</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Current ODO</p>
                    <p>{selectedOdoAlert.current_odo} km</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">Recorded: {formatDateDisplay(selectedOdoAlert.created_at)}</p>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Info className="h-4 w-4" />
                <p>New alerts will continue to appear regardless of your action here.</p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:justify-between">
            <Button 
              variant="outline" 
              onClick={() => {
                setOdoAlertDialogOpen(false);
                if (selectedOdoAlert) {
                  navigate(`/battery-swaps?swap_id=${selectedOdoAlert.swap_id}`);
                }
              }}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              View Swap Details
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleKeepOdoAlert}
                className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
              >
                <Check className="h-4 w-4" />
                Keep
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteOdoAlert}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Ignore & Delete
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert List Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAlert && getAlertIcon(selectedAlert.type)}
              {selectedAlert?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedAlert?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedAlert && selectedAlert.items && selectedAlert.items.length > 0 && (
            <div className="mt-4">
              <div className="space-y-2">
                {selectedAlert.items.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleViewDetails(item)}
                  >
                    <div className="flex-1">
                      {/* Motorcycle Items */}
                      {(selectedAlert.category === 'insurance' || selectedAlert.category === 'registration') && (
                        <div>
                          <div className="font-medium text-sm">
                            {item.plate_number} - {item.model_name || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {selectedAlert.category === 'insurance' ? 'Insurance' : 'Registration'} expires: {formatDate(item.insurance_expiry_date || item.registration_expiry_date)}
                          </div>
                        </div>
                      )}
                      
                      {/* Rider Items */}
                      {(selectedAlert.category.includes('license') || selectedAlert.category.includes('passport') || 
                        selectedAlert.category.includes('emirates') || selectedAlert.category.includes('visa')) && (
                        <div>
                          <div className="font-medium text-sm">
                            {item.name}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Mobile: {item.mobile_uae} | 
                            Expires: {formatDate(item.license_expiry_date || item.passport_expiry_date || item.emirates_id_expiry_date || item.visa_expiry_date)}
                          </div>
                        </div>
                      )}
                    </div>

                    <Eye className="h-4 w-4 text-gray-400" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Item Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {itemDetails && (itemDetails.plate_number ? 
                `Motorcycle: ${itemDetails.plate_number}` : 
                `Rider: ${itemDetails.name}`)}
            </DialogTitle>
            <DialogDescription>
              Complete details for this item
            </DialogDescription>
          </DialogHeader>

          {itemDetails && (
            <div className="mt-4 space-y-4">
              {/* Motorcycle Details */}
              {itemDetails.plate_number && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Plate Number</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.plate_number}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Model</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.model_name} {itemDetails.model_version}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Chassis Number</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.chassis_number}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Color</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.color}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Status</label>
                    <p className="text-sm font-medium mt-1 capitalize">{itemDetails.status}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Lease Status</label>
                    <p className="text-sm font-medium mt-1 capitalize">{itemDetails.lease_status}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Insurance Provider</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.insurance_provider || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Insurance Expiry</label>
                    <p className="text-sm font-medium mt-1">{formatDate(itemDetails.insurance_expiry_date)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Registration Expiry</label>
                    <p className="text-sm font-medium mt-1">{formatDate(itemDetails.registration_expiry_date)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">City</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.city}</p>
                  </div>
                  {itemDetails.lessee_name && (
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-gray-500">Lessee</label>
                      <p className="text-sm font-medium mt-1">{itemDetails.lessee_name}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Rider Details */}
              {itemDetails.name && !itemDetails.plate_number && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Name</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Mobile (UAE)</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.mobile_uae}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Email</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.email}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Emirates ID</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.emirates_id}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">License Number</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.license_number}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">License Expiry</label>
                    <p className="text-sm font-medium mt-1">{formatDate(itemDetails.license_expiry_date)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Passport Number</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.passport_number}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Passport Expiry</label>
                    <p className="text-sm font-medium mt-1">{formatDate(itemDetails.passport_expiry_date)}</p>
                  </div>
                  {itemDetails.visa_expiry_date && (
                    <>
                      <div>
                        <label className="text-xs font-medium text-gray-500">Visa Number</label>
                        <p className="text-sm font-medium mt-1">{itemDetails.visa_number || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">Visa Expiry</label>
                        <p className="text-sm font-medium mt-1">{formatDate(itemDetails.visa_expiry_date)}</p>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="text-xs font-medium text-gray-500">Platform</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.platform}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Status</label>
                    <p className="text-sm font-medium mt-1 capitalize">{itemDetails.status}</p>
                  </div>
                  {itemDetails.nationality && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Nationality</label>
                      <p className="text-sm font-medium mt-1">{itemDetails.nationality}</p>
                    </div>
                  )}
                  {itemDetails.lessee_name && (
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-gray-500">Lessee</label>
                      <p className="text-sm font-medium mt-1">{itemDetails.lessee_name}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Overrule pending — render the alert payload directly */}
              {itemDetails._detail_kind === 'overrule' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Rider</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.rider_name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Bike Plate</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.bike_plate_number || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Warning Type</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.warning_type || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Correction Due</label>
                    <p className="text-sm font-medium mt-1">{formatDate(itemDetails.correction_due_date)}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-gray-500">Overruled By</label>
                    <p className="text-sm font-medium mt-1">{itemDetails.overruled_by || '-'}</p>
                  </div>
                </div>
              )}

              {/* Generic fallback — render every non-empty field from the
                  alert payload so the dialog is never blank. */}
              {itemDetails._detail_kind === 'generic' && (
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(itemDetails)
                    .filter(([k, v]) => k !== '_detail_kind' && k !== 'id' && v !== null && v !== undefined && v !== '')
                    .map(([k, v]) => (
                      <div key={k}>
                        <label className="text-xs font-medium text-gray-500 capitalize">
                          {k.replace(/_/g, ' ')}
                        </label>
                        <p className="text-sm font-medium mt-1">
                          {typeof v === 'string' && /\d{4}-\d{2}-\d{2}/.test(v) ? formatDate(v) : String(v)}
                        </p>
                      </div>
                    ))}
                </div>
              )}
              {/* Info message */}
              <div className="flex items-center gap-2 text-sm text-gray-600 mt-4 pt-4 border-t">
                <Info className="h-4 w-4" />
                <p>New alerts will continue to appear for expiring documents regardless of your action here.</p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:justify-between mt-4">
            <Button 
              variant="outline" 
              onClick={handleNavigateToItem}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              View {itemDetails?.plate_number ? 'Motorcycle' : 'Rider'}
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDetailDialogOpen(false);
                  setSelectedItem(null);
                  setItemDetails(null);
                }}
                className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
              >
                <Check className="h-4 w-4" />
                Keep
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDismissAlert}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Ignore & Delete
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CriticalAlerts;
